import { getAuth } from 'firebase/auth'
import { nanoid } from 'nanoid'
import * as Sentry from '@sentry/browser'
import { kPaxtonAppApiRESTBaseUrl } from '@/constants/constants-links'
import { formatUnknownError, getResponseDebugContext, getTopLevelKeys, readResponseJson } from '@/util/api/response-body'
import { ApiError } from '@/util/errors/api-error'
import { ApiResponsePayloadError } from '@/util/errors/api-response-payload-error'
import { kUserFilesMoveApiBasePath } from '../constants'
import { MoveSelectionRequest, MoveSelectionResponse, MoveSelectionResponseSchema } from '../schemas'
import { createUserFilesApiError, formatUserFilesErrorDetail, isExpectedUserFilesApiError } from '@/user_files/utils/api-error'

export async function postMoveSelection(request: MoveSelectionRequest): Promise<MoveSelectionResponse> {
  const sentryTransactionId = nanoid()

  try {
    const hasFiles = (request.file_ids?.length ?? 0) > 0
    const hasFolders = (request.folder_ids?.length ?? 0) > 0
    if (!hasFiles && !hasFolders) {
      throw new Error('Move request must include at least one file_id or folder_id')
    }

    const auth = getAuth()
    const token = await auth.currentUser?.getIdToken()

    if (!token) {
      throw new Error('User not authenticated')
    }

    const url = `${kPaxtonAppApiRESTBaseUrl()}${kUserFilesMoveApiBasePath}/`

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        'X-Transaction-Id': sentryTransactionId,
      },
      body: JSON.stringify({
        destination_folder_id: request.destination_folder_id,
        file_ids: request.file_ids ?? [],
        folder_ids: request.folder_ids ?? [],
        ...(request.file_preconditions ? { file_preconditions: request.file_preconditions } : {}),
        ...(request.folder_preconditions ? { folder_preconditions: request.folder_preconditions } : {}),
        ...(request.destination_folder_precondition ? { destination_folder_precondition: request.destination_folder_precondition } : {}),
      }),
    })

    const { data, parseError } = await readResponseJson(response)
    if (!response.ok) {
      const errorData = parseError ? {} : data
      const error = createUserFilesApiError({ status: response.status, data: errorData, fallbackMessage: `Failed to move selection: ${response.status}` })
      const detail = formatUserFilesErrorDetail(errorData)

      if (!isExpectedUserFilesApiError(error)) {
        Sentry.withScope((scope) => {
          scope.setTags({ transaction_id: sentryTransactionId })
          Sentry.captureException(new Error(`Fetch error - status: ${response.status}, detail: ${detail}`), {
            extra: {
              ...getResponseDebugContext(response, url),
              requestBody: request,
              responseData: errorData,
              parseError: parseError ? formatUnknownError(parseError) : null,
            },
          })
        })
      }

      throw error
    }

    if (parseError) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentryTransactionId })
        Sentry.captureException(new Error('Move selection response JSON parse failed'), {
          extra: {
            ...getResponseDebugContext(response, url),
            requestBody: request,
            parseError: formatUnknownError(parseError),
          },
        })
      })

      throw new ApiResponsePayloadError(response.status, 'Invalid move selection response JSON')
    }

    const parsed = MoveSelectionResponseSchema.safeParse(data)
    if (!parsed.success) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentryTransactionId })
        Sentry.captureException(new Error('Move selection response schema validation failed'), {
          extra: {
            ...getResponseDebugContext(response, url),
            requestBody: request,
            responseKeys: getTopLevelKeys(data),
            issues: parsed.error.issues,
          },
        })
      })

      throw new ApiResponsePayloadError(response.status, 'Invalid move selection response shape', parsed.error.issues)
    }

    return parsed.data
  } catch (error) {
    if (!(error instanceof ApiError)) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentryTransactionId })
        Sentry.captureException(error, {
          extra: {
            requestBody: request,
          },
        })
      })
    }
    throw error
  }
}
